import { z } from 'zod';
import { LowercaseUuidSchema } from './common.js';
import { OAuthProviderSchema } from './auth.js';
// =============================================================================
// Chat cards - interactive cards shown under an assistant reply (web chat)
// =============================================================================
/** What a connect card connects: an email inbox or a calendar. */
export const ConnectCardTargetSchema = z.enum(['email', 'calendar']);
/**
 * A card the agent offered under a reply. Stored as `metadata.card` on the reply's
 * row: one per reply, and only what was asked for.
 *
 * - `connect`: connects an inbox or calendar. `provider` is set only when the user
 *   named one, and scopes the card to a button for it; a card without one offers both.
 *   Cards stored before `provider` existed have none.
 * - `verify-phone`: verifies the user's WhatsApp number, or changes a verified one.
 *   The card collects the number and the code itself, so it carries no fields.
 */
export const ChatCardSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('connect'), target: ConnectCardTargetSchema, provider: OAuthProviderSchema.optional() }),
    z.object({ kind: z.literal('verify-phone') }),
]);
/**
 * The card a message's metadata holds, or null when it holds none. Metadata is
 * untyped JSON at rest, so a row that predates cards or carries a malformed `card`
 * reads as a plain reply rather than failing the reader.
 */
export function messageCard(metadata) {
    if (!metadata || typeof metadata !== 'object' || !('card' in metadata))
        return null;
    const parsed = ChatCardSchema.safeParse(metadata.card);
    return parsed.success ? parsed.data : null;
}
/**
 * Custom SSE event carrying the card for the reply that just finished, sent before
 * RUN_FINISHED. `messageId` is the reply's persisted id, the id history returns it
 * under.
 */
export const CHAT_CARD_EVENT = 'card';
export const ChatCardEventSchema = z.object({
    type: z.literal(CHAT_CARD_EVENT),
    messageId: z.string(),
    card: ChatCardSchema,
});
// =============================================================================
// Connect outcome - how a connect attempt started from a card ended
// =============================================================================
/**
 * - `connected`: the connection exists.
 * - `cancelled`: the user backed out at the provider's consent screen.
 * - `refused`: the user completed consent but left out a permission Bill needs.
 * - `failed`: anything else went wrong.
 */
export const ConnectOutcomeStatusSchema = z.enum(['connected', 'cancelled', 'refused', 'failed']);
/**
 * What the client knows about why an attempt did not connect: the provider's error code
 * or the API error's code, and its message. Both optional, and quoted to the agent as
 * reported data, never trusted as wording.
 */
export const CONNECT_OUTCOME_FAILURE_CODE_MAX_LENGTH = 64;
export const CONNECT_OUTCOME_FAILURE_MESSAGE_MAX_LENGTH = 300;
export const ConnectOutcomeFailureSchema = z.strictObject({
    code: z.string().min(1).max(CONNECT_OUTCOME_FAILURE_CODE_MAX_LENGTH).optional(),
    message: z.string().min(1).max(CONNECT_OUTCOME_FAILURE_MESSAGE_MAX_LENGTH).optional(),
});
/**
 * `metadata.connectOutcome` on the row the server stores for an outcome. `connectionId`
 * is null for every status but `connected`; `provider` is the connection's own for
 * `connected` and the one the user chose otherwise. `failure` is present only when the
 * client reported a detail with a non-connected outcome; rows stored before it existed
 * have none.
 */
export const ConnectOutcomeSchema = z.object({
    cardMessageId: z.string(),
    kind: ConnectCardTargetSchema,
    provider: OAuthProviderSchema,
    status: ConnectOutcomeStatusSchema,
    connectionId: z.string().nullable(),
    failure: ConnectOutcomeFailureSchema.optional(),
});
export const ConnectOutcomeMetadataSchema = z.object({ connectOutcome: ConnectOutcomeSchema });
/**
 * The connect outcome a message's metadata holds, or null when it holds none or a
 * malformed one (see {@link messageCard}).
 */
export function messageConnectOutcome(metadata) {
    const parsed = ConnectOutcomeMetadataSchema.safeParse(metadata);
    return parsed.success ? parsed.data.connectOutcome : null;
}
/**
 * What the client reports about how a connect attempt ended. Every outcome carries the
 * `attemptId` the card minted for that attempt, which tells a second attempt from a
 * replay of the first. A `connected` outcome names the connection, whose provider the
 * server reads for itself; any other outcome carries the provider the user chose, and
 * optionally the `failure` detail the client has.
 */
export const ConnectOutcomeReportSchema = z.discriminatedUnion('status', [
    z.strictObject({ status: z.literal('connected'), connectionId: LowercaseUuidSchema, attemptId: z.uuid() }),
    z.strictObject({
        status: ConnectOutcomeStatusSchema.exclude(['connected']),
        provider: OAuthProviderSchema,
        attemptId: z.uuid(),
        failure: ConnectOutcomeFailureSchema.optional(),
    }),
]);
/**
 * Report how a connect attempt started from the card on reply `messageId` ended. The
 * server words the stored row itself, so the client supplies no text of its own, only the
 * failure detail it was given.
 */
export const ChatConnectOutcomeStreamRequestSchema = z.strictObject({
    runId: z.string(),
    conversationId: z.string(),
    /** The reply the connect card is on. */
    messageId: z.string().min(1),
    kind: ConnectCardTargetSchema,
    outcome: ConnectOutcomeReportSchema,
});
// =============================================================================
// Phone outcome - a WhatsApp number verified from a card
// =============================================================================
/**
 * `metadata.phoneOutcome` on the row the server stores when the user verifies their
 * WhatsApp number from a `verify-phone` card. Only a verification is recorded: a wrong
 * code, a used-up code or a send refused by the rate limit is handled inside the card.
 * `verifiedAt` is the user's `phoneVerifiedAt` as the server read it (ISO 8601); the
 * number itself is never stored on the row.
 */
export const PhoneOutcomeSchema = z.object({
    cardMessageId: z.string(),
    status: z.literal('verified'),
    verifiedAt: z.iso.datetime(),
});
export const PhoneOutcomeMetadataSchema = z.object({ phoneOutcome: PhoneOutcomeSchema });
/**
 * The phone outcome a message's metadata holds, or null when it holds none or a
 * malformed one (see {@link messageCard}).
 */
export function messagePhoneOutcome(metadata) {
    const parsed = PhoneOutcomeMetadataSchema.safeParse(metadata);
    return parsed.success ? parsed.data.phoneOutcome : null;
}
/**
 * Report that the user verified their number from the card on reply `messageId`. The
 * request carries no outcome: the server reads the verification from the user's own
 * record, so a client cannot report one that did not happen.
 */
export const ChatPhoneOutcomeStreamRequestSchema = z.strictObject({
    runId: z.string(),
    conversationId: z.string(),
    /** The reply the `verify-phone` card is on. */
    messageId: z.string().min(1),
});
